import {
  documentToPlainText,
  emptyDocument,
  normalizeDocument,
} from "@tribunalea/document-editor/document";
import { normalizeCitationReferences } from "@tribunalea/shared";

export const JUSTINIANO_DRAFT_STORAGE_PREFIX = "tribunalea:justiniano-draft";
export const JUSTINIANO_DRAFTS_STORAGE_PREFIX = "tribunalea:justiniano-drafts";
const DRAFTS_SCHEMA_VERSION = 2;
const DEFAULT_DRAFT_TITLE = "Nueva redacción";

let draftSequence = 0;

function storageScope(appId) {
  return String(appId || "default").trim() || "default";
}

function isoDate(value, fallback = new Date().toISOString()) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date.toISOString();
}

function localDraftId(now = Date.now()) {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  draftSequence += 1;
  return `draft-${now}-${draftSequence}`;
}

function inferredDraftTitle(document) {
  const firstLine = documentToPlainText(document)
    .split("\n")
    .map((line) => line.trim())
    .find(Boolean);
  return firstLine?.slice(0, 120) || "Redacción recuperada";
}

function storedTitle(value, fallback = DEFAULT_DRAFT_TITLE) {
  if (typeof value !== "string") return fallback;
  return value.replace(/[\r\n]+/g, " ").slice(0, 160);
}

function copiedTitle(value) {
  const suffix = " (copia)";
  const base = storedTitle(value, "Redacción sin título").trim()
    || "Redacción sin título";
  return `${base.slice(0, 160 - suffix.length)}${suffix}`;
}

function copiedDocument(value) {
  const document = normalizeDocument(value);
  if (typeof globalThis.structuredClone === "function") {
    return globalThis.structuredClone(document);
  }
  return JSON.parse(JSON.stringify(document));
}

function normalizeDraft(value, index = 0) {
  if (!value || typeof value !== "object") return null;
  const now = new Date().toISOString();
  const createdAt = isoDate(value.createdAt, now);
  return {
    id: String(value.id || `draft-${index + 1}`),
    title: storedTitle(value.title, inferredDraftTitle(value.document)),
    document: normalizeDocument(value.document),
    references: normalizeCitationReferences(value.references).slice(0, 50),
    includeSignature: value.includeSignature === true,
    createdAt,
    updatedAt: isoDate(value.updatedAt, createdAt),
  };
}

function normalizeDrafts(values) {
  const seen = new Set();
  return (Array.isArray(values) ? values : [])
    .map(normalizeDraft)
    .filter((draft) => {
      if (!draft || seen.has(draft.id)) return false;
      seen.add(draft.id);
      return true;
    });
}

export function justinianoDraftStorageKey(appId) {
  return `${JUSTINIANO_DRAFT_STORAGE_PREFIX}:${storageScope(appId)}`;
}

export function justinianoDraftsStorageKey(appId) {
  return `${JUSTINIANO_DRAFTS_STORAGE_PREFIX}:${storageScope(appId)}`;
}

export function createJustinianoDraft({
  id = localDraftId(),
  title = DEFAULT_DRAFT_TITLE,
  document = emptyDocument(),
  references = [],
  includeSignature = false,
  createdAt = new Date().toISOString(),
  updatedAt = createdAt,
} = {}) {
  return normalizeDraft({
    id,
    title,
    document,
    references,
    includeSignature,
    createdAt,
    updatedAt,
  });
}

export function cloneJustinianoDraft(source, {
  id,
  title = copiedTitle(source?.title),
  createdAt = new Date().toISOString(),
} = {}) {
  return createJustinianoDraft({
    ...(id ? { id } : {}),
    title,
    document: copiedDocument(source?.document),
    references: normalizeCitationReferences(source?.references),
    includeSignature: source?.includeSignature === true,
    createdAt,
    updatedAt: createdAt,
  });
}

export function persistJustinianoDrafts(appId, drafts, {
  storage = typeof window === "undefined" ? null : window.localStorage,
} = {}) {
  const normalized = normalizeDrafts(drafts);
  try {
    storage?.setItem(
      justinianoDraftsStorageKey(appId),
      JSON.stringify({
        version: DRAFTS_SCHEMA_VERSION,
        drafts: normalized,
      }),
    );
  } catch {
    // La biblioteca sigue siendo utilizable aunque el almacenamiento esté
    // lleno o no esté habilitado por el navegador.
  }
  return normalized;
}

export function readJustinianoDrafts(appId, {
  storage = typeof window === "undefined" ? null : window.localStorage,
} = {}) {
  try {
    const serialized = storage?.getItem(justinianoDraftsStorageKey(appId));
    if (serialized) {
      const parsed = JSON.parse(serialized);
      return normalizeDrafts(Array.isArray(parsed) ? parsed : parsed?.drafts);
    }
  } catch {
    // Si la colección nueva está dañada, todavía intentamos recuperar el
    // borrador usado por la versión anterior.
  }

  try {
    const legacy = storage?.getItem(justinianoDraftStorageKey(appId));
    if (!legacy) return [];
    const document = normalizeDocument(JSON.parse(legacy));
    const migrated = createJustinianoDraft({
      id: "legacy-draft",
      title: inferredDraftTitle(document),
      document,
    });
    persistJustinianoDrafts(appId, [migrated], { storage });
    return [migrated];
  } catch {
    return [];
  }
}

export function justinianoDraftCatalog(appId, options = {}) {
  return readJustinianoDrafts(appId, options)
    .sort((left, right) => (
      new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime()
    ))
    .slice(0, 100)
    .map((draft) => ({
      id: draft.id,
      title: String(draft.title || "").trim() || "Redacción sin título",
      excerpt: documentToPlainText(draft.document)
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 800),
      updatedAt: draft.updatedAt,
    }));
}

// Compatibilidad con integraciones que todavía consumen el borrador único.
export function readJustinianoDraft(appId, options = {}) {
  return readJustinianoDrafts(appId, options)[0]?.document || emptyDocument();
}

export function persistJustinianoDraft(appId, document, {
  storage = typeof window === "undefined" ? null : window.localStorage,
} = {}) {
  const normalized = normalizeDocument(document);
  try {
    storage?.setItem(
      justinianoDraftStorageKey(appId),
      JSON.stringify(normalized),
    );
  } catch {
    // Conserva el comportamiento tolerante de la versión anterior.
  }
  return normalized;
}
